import { HttpResponse, http } from "msw";
import { getHotelInfoResponseMock } from "./get-hotel-info-200.js";
import { hotelsInfoResponseFake } from "./get-hotels-info-200.js";
import { apiPaths } from "../../api-paths.js";
import { getHotelAvailabilityResponseMock } from "./get-hotel-availability-200.js";
import {
  getHotelDealsResponseMock,
  createHotelDealsResponseMock,
  createHotelDealsResponseWithRoomIdsMock,
} from "./get-hotel-deals-200.js";
import { patchHotelDealsResponseForCurrencyAndNightlyPrice } from "./patch-hotel-deals-response-currency.js";
import type { Hotels_GetHotelDeals_V2_GetHotelDealsQueryV2Response_Success } from "../../swagger-types/v1/index.js";
import type { Hotels_GetHotelInfo_GetHotelInfoQueryResponse } from "../../swagger-types/v1/index.js";
import { cloneDeep } from "lodash-es";
export { roomsInfoHandlersFactory } from "./rooms-info-msw-handlers.js";

export const hotelInfoHandlersFactory = {
  create200: (
    transformer?: (
      response: Hotels_GetHotelInfo_GetHotelInfoQueryResponse,
    ) => Promise<Hotels_GetHotelInfo_GetHotelInfoQueryResponse> | Hotels_GetHotelInfo_GetHotelInfoQueryResponse,
  ) => [
    http.get(`**${apiPaths.hotels.getHotelInfo}`, async () => {
      const response = cloneDeep(getHotelInfoResponseMock().default);
      const body = transformer ? await transformer(response) : response;

      return HttpResponse.json(body, { status: 200 });
    }),
  ],
};

export const hotelInfoHandlers = {
  "200": hotelInfoHandlersFactory.create200(),
};

export const hotelsInfoHandlers = {
  "200": [
    http.get(`**${apiPaths.hotels.getHotelsInfo}`, async () =>
      HttpResponse.json(hotelsInfoResponseFake, { status: 200 }),
    ),
  ],
};
export const hotelAvailabilityHandlers = {
  "200": [
    http.get(`**${apiPaths.hotels.getHotelsAvailability}`, async () =>
      HttpResponse.json(getHotelAvailabilityResponseMock().default, { status: 200 }),
    ),
  ],
};

type HotelSourceLinksResponseEntry = string | { url?: string; description?: string };

export const hotelSourceLinksHandlersFactory = {
  create200: (links: Record<string, HotelSourceLinksResponseEntry> = {}) => [
    http.get(`**${apiPaths.hotels.getHotelsSourceLinks}`, async () => HttpResponse.json(links, { status: 200 })),
  ],
};

export const hotelSourceLinksHandlers = {
  "200": hotelSourceLinksHandlersFactory.create200(),
};

type RequestKeyPollingResponseConfig = {
  requestKey: string | null;
  isCompleted?: boolean;
  roomName?: string;
};

export const hotelDealsHandlersFactory = {
  create200: (roomName: string) => [
    http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () =>
      HttpResponse.json(createHotelDealsResponseMock(roomName), { status: 200 }),
    ),
  ],
  createIncompleteV2: () => [
    http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () =>
      HttpResponse.json(createIncompleteHotelDealsResponse(), { status: 200 }),
    ),
  ],
  createErrorV2: () => [
    http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () => {
      return HttpResponse.error();
    }),
  ],
  create500ErrorV2: () => [
    http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () => {
      return HttpResponse.json({}, { status: 500 });
    }),
  ],
  createRequestKeyPollingV2: (responses: ReadonlyArray<RequestKeyPollingResponseConfig>) => {
    const responsesQueue =
      responses.length > 0
        ? responses
        : [
            {
              requestKey: null,
              isCompleted: false,
            },
          ];
    let callIndex = 0;

    return [
      http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () => {
        const responseIndex = Math.min(callIndex, responsesQueue.length - 1);
        const response = responsesQueue[responseIndex];
        callIndex += 1;

        const body: Hotels_GetHotelDeals_V2_GetHotelDealsQueryV2Response_Success = {
          ...createHotelDealsResponseMock(response.roomName ?? String(callIndex)),
          requestKey: response.requestKey,
          isCompleted: response.isCompleted ?? false,
        };

        return HttpResponse.json(body, { status: 200 });
      }),
    ];
  },
  createRoomIdsPollingV2: (
    responses: ReadonlyArray<{
      roomIds: number[];
      requestKey?: string | null;
      isCompleted?: boolean;
      dealRoomIds?: Array<number | null>;
    }>,
  ) => {
    const responsesQueue =
      responses.length > 0
        ? responses
        : [
            {
              roomIds: [],
              requestKey: null,
              isCompleted: true,
            },
          ];
    let callIndex = 0;

    return [
      http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () => {
        const response = responsesQueue[Math.min(callIndex, responsesQueue.length - 1)];
        callIndex += 1;

        return HttpResponse.json(
          createHotelDealsResponseWithRoomIdsMock(response.roomIds, {
            requestKey: response.requestKey,
            isCompleted: response.isCompleted,
            dealRoomIds: response.dealRoomIds,
          }),
          { status: 200 },
        );
      }),
    ];
  },

  matchingRequestCurrency: (roomName: string, nightlyPrice: number) => [
    http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async ({ request }) => {
      const url = new URL(request.url);
      const currencyCode = url.searchParams.get("currencyCode") ?? "USD";
      const body = cloneDeep(createHotelDealsResponseMock(roomName));
      patchHotelDealsResponseForCurrencyAndNightlyPrice(body, currencyCode, nightlyPrice);
      return HttpResponse.json(body, { status: 200 });
    }),
  ],
  "200CancellationPolicyEnforced": () => [
    http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () =>
      HttpResponse.json(getHotelDealsResponseMock().cancellationPolicyEnforced, { status: 200 }),
    ),
  ],
  "200CheapestDealNotAvailable": (
    transformer?: (
      response: Hotels_GetHotelDeals_V2_GetHotelDealsQueryV2Response_Success,
    ) => Promise<Hotels_GetHotelDeals_V2_GetHotelDealsQueryV2Response_Success>,
  ) => [
    http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () => {
      const body = transformer
        ? await transformer(cloneDeep(getHotelDealsResponseMock().cheapestDealNotAvailable))
        : getHotelDealsResponseMock().cheapestDealNotAvailable;
      return HttpResponse.json(body, { status: 200 });
    }),
  ],
  createSingleRoomTypeV2: () => [
    http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () => {
      const response = cloneDeep(getHotelDealsResponseMock().default);
      const deals = response.deals.filter((deal) => deal.roomIndex === 1);
      const roomIds = [...new Set(deals.flatMap((deal) => (deal.roomId == null ? [] : [deal.roomId])))];

      return HttpResponse.json(
        {
          ...response,
          deals,
          hotelRooms: response.hotelRooms.slice(0, 1),
          roomIds,
          isCompleted: true,
        },
        { status: 200 },
      );
    }),
  ],
  "200Test": () => [
    http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () => {
      const body: Hotels_GetHotelDeals_V2_GetHotelDealsQueryV2Response_Success = {
        isCompleted: true,
        requestKey: null,
        rateModel: "Net",
        deals: [
          {
            cancellationPolicyEnforced: null,
            checkoutId: "c59411c1-88cb-45f5-9354-173c6472fd6b",
            dealType: "FullRange",
            roomName: "Standard Double Bed Room 1234",
            roomCode: "1ed67198-e953-49fd-af06-de905b52efa6",
            boardCode: "RoomOnly",
            boardDescription: "Room Only",
            checkIn: "2025-11-13",
            checkOut: "2025-11-14",
            cancellationPolicy: {
              type: "NonRefundable",
              deadline: null,
              penalties: [
                {
                  from: "2025-11-13T13:36:33.973827Z",
                  to: null,
                  amount: {
                    amount: 236.0,
                    currency: "USD",
                  },
                },
              ],
              description:
                "This room is non-refundable and cannot be changed. No-shows and early checkouts are not eligible for a refund.",
            },
            fees: {
              total: 63.84,
              currencyCode: "USD",
              items: [
                {
                  code: "CityTax",
                  amount: 3.5,
                  currencyCode: "USD",
                  description: "government tax",
                },
                {
                  code: "CityTax",
                  amount: 45.9,
                  currencyCode: "USD",
                  description: "Destination fee",
                },
                {
                  code: "CityTax",
                  amount: 14.44,
                  currencyCode: "USD",
                  description: "City tax",
                },
              ],
            },
            taxes: {
              total: 9.56,
              currencyCode: "USD",
              items: [
                {
                  code: "Vat",
                  amount: 9.56,
                  currencyCode: "USD",
                  description: "Tax",
                },
              ],
            },
            hotelFees: {
              total: 0,
              currencyCode: "USD",
              items: [],
            },
            hotelFeeTaxes: {
              total: 0,
              currencyCode: "USD",
              items: [],
            },
            rooms: [
              {
                roomCode: "1ed67198-e953-49fd-af06-de905b52efa6",
                roomName: "Standard Double Room",
                boardCode: "HalfBoard",
                boardDescription: "Half Board",
                checkIn: "2025-11-13",
                checkOut: "2025-11-14",
              },
            ],
            dealPrice: {
              currencyCode: "USD",
              nightlyPrice: 162.6,
              totalRoomsPrice: 162.6,
              totalPrice: 236.0,
              totalPriceWithHotelFees: 236.0,
              totalRoomsPriceWithHotelFees: 162.6,
            },
            verificationStatus: "Verified",
            dealIndex: 1,
            roomDealIndex: 1,
            roomIndex: 1,
            roomId: 101,
          },
          {
            cancellationPolicyEnforced: null,
            checkoutId: "188b2d7c-2eb3-41ec-bd99-4c7fa0244a28",
            dealType: "FullRange",
            roomName: "Standard Double Bed Room",
            roomCode: "1ed67198-e953-49fd-af06-de905b52efa6",
            boardCode: "HalfBoard",
            boardDescription: "Half Board",
            checkIn: "2025-11-13",
            checkOut: "2025-11-14",
            cancellationPolicy: {
              type: "NonRefundable",
              deadline: null,
              penalties: [
                {
                  from: "2025-11-12T01:36:33.8648533Z",
                  to: null,
                  amount: {
                    amount: 248.01,
                    currency: "USD",
                  },
                },
              ],
              description:
                "This room is non-refundable and cannot be changed. No-shows and early checkouts are not eligible for a refund.",
            },
            fees: {
              total: 64.77,
              currencyCode: "USD",
              items: [
                {
                  code: "CityTax",
                  amount: 3.5,
                  currencyCode: "USD",
                  description: "government tax",
                },
                {
                  code: "CityTax",
                  amount: 45.9,
                  currencyCode: "USD",
                  description: "Destination fee",
                },
                {
                  code: "CityTax",
                  amount: 15.37,
                  currencyCode: "USD",
                  description: "City tax",
                },
              ],
            },
            taxes: {
              total: 10.18,
              currencyCode: "USD",
              items: [
                {
                  code: "Vat",
                  amount: 10.18,
                  currencyCode: "USD",
                  description: "Tax",
                },
              ],
            },
            hotelFees: {
              total: 0,
              currencyCode: "USD",
              items: [],
            },
            hotelFeeTaxes: {
              total: 0,
              currencyCode: "USD",
              items: [],
            },
            rooms: [
              {
                roomCode: "1ed67198-e953-49fd-af06-de905b52efa6",
                roomName: "Standard Double Room",
                boardCode: "HalfBoard",
                boardDescription: "Half Board",
                checkIn: "2025-11-13",
                checkOut: "2025-11-14",
              },
            ],
            dealPrice: {
              currencyCode: "USD",
              nightlyPrice: 173.06,
              totalRoomsPrice: 173.06,
              totalPrice: 248.01,
              totalPriceWithHotelFees: 248.01,
              totalRoomsPriceWithHotelFees: 173.06,
            },
            verificationStatus: "Verified",
            dealIndex: 2,
            roomDealIndex: 2,
            roomIndex: 1,
            roomId: 101,
          },
          {
            cancellationPolicyEnforced: null,
            checkoutId: "ff731b0c-1057-41d8-94cc-61aed29e63af",
            dealType: "FullRange",
            roomName: "Deluxe Room",
            roomCode: "e8509719-e461-4749-bdb5-8bf2594b895d",
            boardCode: "BedAndBreakfast",
            boardDescription: "Bed And Breakfast",
            checkIn: "2025-11-13",
            checkOut: "2025-11-14",
            cancellationPolicy: {
              type: "NonRefundable",
              deadline: null,
              penalties: [
                {
                  from: "2025-11-12T01:36:33.8648531Z",
                  to: null,
                  amount: {
                    amount: 347.0,
                    currency: "USD",
                  },
                },
              ],
              description:
                "This room is non-refundable and cannot be changed. No-shows and early checkouts are not eligible for a refund.",
            },
            fees: {
              total: 72.43,
              currencyCode: "USD",
              items: [
                {
                  code: "CityTax",
                  amount: 3.5,
                  currencyCode: "USD",
                  description: "government tax",
                },
                {
                  code: "CityTax",
                  amount: 45.9,
                  currencyCode: "USD",
                  description: "Destination fee",
                },
                {
                  code: "CityTax",
                  amount: 23.03,
                  currencyCode: "USD",
                  description: "City tax",
                },
              ],
            },
            taxes: {
              total: 15.25,
              currencyCode: "USD",
              items: [
                {
                  code: "Vat",
                  amount: 15.25,
                  currencyCode: "USD",
                  description: "Tax",
                },
              ],
            },
            hotelFees: {
              total: 0,
              currencyCode: "USD",
              items: [],
            },
            hotelFeeTaxes: {
              total: 0,
              currencyCode: "USD",
              items: [],
            },
            rooms: [
              {
                roomCode: "e8509719-e461-4749-bdb5-8bf2594b895d",
                roomName: "Deluxe Room",
                boardCode: "BedAndBreakfast",
                boardDescription: "Bed And Breakfast",
                checkIn: "2025-11-13",
                checkOut: "2025-11-14",
              },
            ],
            dealPrice: {
              currencyCode: "USD",
              nightlyPrice: 259.32,
              totalRoomsPrice: 259.32,
              totalPrice: 347.0,
              totalPriceWithHotelFees: 347.0,
              totalRoomsPriceWithHotelFees: 259.32,
            },
            verificationStatus: "Unknown",
            dealIndex: 3,
            roomDealIndex: 1,
            roomIndex: 2,
            roomId: 202,
          },
          {
            cancellationPolicyEnforced: null,
            checkoutId: "1b7812ca-9eee-411e-b14e-40065cdc5f66",
            dealType: "FullRange",
            roomName: "Standard Room",
            roomCode: "8592586c-fc3a-4bb5-aae0-ac93d8ccee67",
            boardCode: "RoomOnly",
            boardDescription: "Room Only",
            checkIn: "2025-11-13",
            checkOut: "2025-11-14",
            cancellationPolicy: {
              type: "NonRefundable",
              deadline: null,
              penalties: [
                {
                  from: "2025-11-12T01:36:33.8648527Z",
                  to: null,
                  amount: {
                    amount: 379.0,
                    currency: "USD",
                  },
                },
              ],
              description:
                "This room is non-refundable and cannot be changed. No-shows and early checkouts are not eligible for a refund.",
            },
            fees: {
              total: 74.9,
              currencyCode: "USD",
              items: [
                {
                  code: "CityTax",
                  amount: 3.5,
                  currencyCode: "USD",
                  description: "government tax",
                },
                {
                  code: "CityTax",
                  amount: 45.9,
                  currencyCode: "USD",
                  description: "Destination fee",
                },
                {
                  code: "CityTax",
                  amount: 25.5,
                  currencyCode: "USD",
                  description: "City tax",
                },
              ],
            },
            taxes: {
              total: 16.89,
              currencyCode: "USD",
              items: [
                {
                  code: "Vat",
                  amount: 16.89,
                  currencyCode: "USD",
                  description: "Tax",
                },
              ],
            },
            hotelFees: {
              total: 0,
              currencyCode: "USD",
              items: [],
            },
            hotelFeeTaxes: {
              total: 0,
              currencyCode: "USD",
              items: [],
            },
            rooms: [
              {
                roomCode: "8592586c-fc3a-4bb5-aae0-ac93d8ccee67",
                roomName: "Standard Room",
                boardCode: "RoomOnly",
                boardDescription: "Room Only",
                checkIn: "2025-11-13",
                checkOut: "2025-11-14",
              },
            ],
            dealPrice: {
              currencyCode: "USD",
              nightlyPrice: 287.21,
              totalRoomsPrice: 287.21,
              totalPrice: 379.0,
              totalPriceWithHotelFees: 379.0,
              totalRoomsPriceWithHotelFees: 287.21,
            },
            verificationStatus: "Unknown",
            dealIndex: 4,
            roomDealIndex: 1,
            roomIndex: 3,
            roomId: 303,
          },
        ],
        hotelRooms: [
          {
            roomCode: "1ed67198-e953-49fd-af06-de905b52efa6",
            roomName: "Standard Double Bed Room",
          },
          {
            roomCode: "e8509719-e461-4749-bdb5-8bf2594b895d",
            roomName: "Deluxe Room",
          },
          {
            roomCode: "8592586c-fc3a-4bb5-aae0-ac93d8ccee67",
            roomName: "Standard Room",
          },
        ],
        cheapestDealAvailabilityState: "NotAvailable",
        roomIds: [101, 202, 303],
      };
      return HttpResponse.json(body, { status: 200 });
    }),
  ],
};

const createIncompleteHotelDealsResponse = (): Hotels_GetHotelDeals_V2_GetHotelDealsQueryV2Response_Success => {
  const response = cloneDeep(getHotelDealsResponseMock().default);
  return {
    ...response,
    isCompleted: false,
  };
};

const getHotelDealsPollingHandler = () => {
  const responseWithCheapestDeal: Hotels_GetHotelDeals_V2_GetHotelDealsQueryV2Response_Success = {
    isCompleted: false,
    requestKey: null,
    rateModel: "AllInclusive",
    deals: [
      {
        cancellationPolicyEnforced: null,
        checkoutId: "5377694d-f235-4f95-9104-470d4cdbc7bc",
        dealType: "FullRange",
        roomName: "Standard Room 1",
        roomCode: "4cdc2bb3-ab9a-4fce-9afc-d6571a01c853",
        boardCode: "HalfBoard",
        boardDescription: "Half Board",
        checkIn: "2025-08-18",
        checkOut: "2025-08-20",
        cancellationPolicy: {
          type: "NonRefundable",
          deadline: null,
          penalties: [
            {
              from: "2025-08-09T05:28:30.7189468Z",
              to: null,
              amount: {
                amount: 276.0,
                currency: "USD",
              },
            },
          ],
          description:
            "This room is non-refundable and cannot be changed. No-shows and early checkouts are not eligible for a refund.",
        },
        fees: {
          total: 112.51,
          currencyCode: "USD",
          items: [
            {
              code: "CityTax",
              amount: 7.0,
              currencyCode: "USD",
              description: "government tax",
            },
            {
              code: "CityTax",
              amount: 91.8,
              currencyCode: "USD",
              description: "Destination fee",
            },
            {
              code: "CityTax",
              amount: 13.71,
              currencyCode: "USD",
              description: "City tax",
            },
          ],
        },
        taxes: {
          total: 9.08,
          currencyCode: "USD",
          items: [
            {
              code: "Vat",
              amount: 9.08,
              currencyCode: "USD",
              description: "Tax",
            },
          ],
        },
        hotelFees: {
          total: 0,
          currencyCode: "USD",
          items: [],
        },
        hotelFeeTaxes: {
          total: 0,
          currencyCode: "USD",
          items: [],
        },
        rooms: [
          {
            roomCode: "4cdc2bb3-ab9a-4fce-9afc-d6571a01c853",
            roomName: "Standard Room",
            boardCode: "HalfBoard",
            boardDescription: "Half Board",
            checkIn: "2025-08-18",
            checkOut: "2025-08-20",
          },
        ],
        dealPrice: {
          currencyCode: "USD",
          nightlyPrice: 77.2,
          totalRoomsPrice: 154.41,
          totalPrice: 276.0,
          totalPriceWithHotelFees: 276.0,
          totalRoomsPriceWithHotelFees: 154.41,
        },
        verificationStatus: "Verified",
        dealIndex: 1,
        roomDealIndex: 1,
        roomIndex: 1,
        roomId: 101,
      },
    ],
    hotelRooms: [
      {
        roomCode: "4cdc2bb3-ab9a-4fce-9afc-d6571a01c853",
        roomName: "Standard Room 1",
      },
    ],
    cheapestDealAvailabilityState: "Available",
    roomIds: [101],
  };
  const responses: Hotels_GetHotelDeals_V2_GetHotelDealsQueryV2Response_Success[] = [
    createHotelDealsResponseMock("1"),
    responseWithCheapestDeal,
    responseWithCheapestDeal,
    {
      isCompleted: false,
      requestKey: null,
      rateModel: "Net",
      deals: [],
      hotelRooms: [],
      cheapestDealAvailabilityState: "Unknown",
      roomIds: [],
    },
    {
      isCompleted: false,
      requestKey: null,
      rateModel: "Net",
      deals: [],
      hotelRooms: [],
      cheapestDealAvailabilityState: "Unknown",
      roomIds: [],
    },
  ];

  return async () => HttpResponse.json(responses.pop(), { status: 200 });
};

export const hotelDealsHandlers = {
  "200": [
    http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () =>
      HttpResponse.json(getHotelDealsResponseMock().default, { status: 200 }),
    ),
  ],
  "200RDM1": [
    http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () =>
      HttpResponse.json(getHotelDealsResponseMock().rdm1, { status: 200 }),
    ),
  ],
  "200CheapestDealNotAvailable": [
    http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () =>
      HttpResponse.json(getHotelDealsResponseMock().cheapestDealNotAvailable, { status: 200 }),
    ),
  ],
  "200CheapestDealUnknown": [
    http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () =>
      HttpResponse.json(getHotelDealsResponseMock().cheapestDealUnknown, { status: 200 }),
    ),
  ],
  "200CheapestDealVerificationFailed": [
    http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () =>
      HttpResponse.json(getHotelDealsResponseMock().cheapestDealVerificationFailed, { status: 200 }),
    ),
  ],
  "200NoDealsFound": [
    http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () =>
      HttpResponse.json(getHotelDealsResponseMock().noDealsFound, { status: 200 }),
    ),
  ],
  "200CancellationPolicyEnforced": [
    http.get(`**${apiPaths.hotels.getHotelDealsV2}`, async () =>
      HttpResponse.json(getHotelDealsResponseMock().cancellationPolicyEnforced, { status: 200 }),
    ),
  ],
  "200Polling": [http.get(`**${apiPaths.hotels.getHotelDealsV2}`, getHotelDealsPollingHandler())],
};
